import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    if (!email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = 'Invalid email format';
    if (!password) newErrors.password = 'Password is required';
    if (twoFactorRequired && !otp.trim()) newErrors.otp = 'Enter the 6-digit code from your authenticator app';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setErrors({});
    setLoading(true);

    try {
      const result = await login(email, password, twoFactorRequired ? otp : undefined);
      if (result && result.twoFactorRequired) {
        // Password accepted; now prompt for the authenticator code.
        setTwoFactorRequired(true);
        return;
      }
      navigate('/dashboard');
    } catch (err) {
      setErrors({ submit: err.response?.data?.error || 'Login failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: theme.bg }}>
      <div className="w-full max-w-md rounded-lg shadow-lg p-8" style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}>
        <h1 className="text-2xl font-bold text-center mb-6" style={{ color: theme.text }}>
          RDA Investment Platform
        </h1>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {!twoFactorRequired ? (
            <>
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={errors.email}
                required
              />

              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(e); }}
                error={errors.password}
                required
              />
            </>
          ) : (
            <>
              <p className="text-sm" style={{ color: theme.textSecondary }}>
                Enter the 6-digit code from your authenticator app to finish signing in.
              </p>
              <Input
                label="Authentication code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(e); }}
                error={errors.otp}
                required
              />
            </>
          )}

          {errors.submit && <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>}

          <Button
            type="submit"
            disabled={loading}
            className="w-full"
          >
            {loading ? 'Signing in...' : (twoFactorRequired ? 'Verify code' : 'Sign In')}
          </Button>

          {twoFactorRequired && (
            <button
              type="button"
              className="w-full text-sm underline"
              style={{ color: theme.textSecondary }}
              onClick={() => { setTwoFactorRequired(false); setOtp(''); setErrors({}); }}
            >
              Back
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
